import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DEFAULT_CHIP_UNIT } from "@/lib/constants";
import { formatChipAmount } from "@/lib/formatting";
import type { CalculationResult } from "@/lib/poker-types";

interface SettlementResultsProps {
  result: CalculationResult;
  chipValueDisplayUnit: number;
  exactChipValueUnit: number;
  formatCurrency: (amount: number) => string;
  formatChipValueCurrency: (amount: number) => string;
  copyState: "idle" | "done" | "error";
  onCopySummary: () => void;
}

export function SettlementResults({
  result,
  chipValueDisplayUnit,
  exactChipValueUnit,
  formatCurrency,
  formatChipValueCurrency,
  copyState,
  onCopySummary,
}: SettlementResultsProps) {
  const isSmallStackGame = result.totalBoughtChips <= DEFAULT_CHIP_UNIT * 10;
  const primaryUnit = isSmallStackGame ? 1 : chipValueDisplayUnit;

  return (
    <Card className="py-4">
      <CardHeader className="gap-2">
        <CardTitle className="text-xl">Settlement plan</CardTitle>
        <CardDescription>
          Chip value {formatChipValueCurrency(result.chipValue * primaryUnit)} per{" "}
          {primaryUnit === 1 ? "chip" : `${formatChipAmount(primaryUnit)} chips`}.
          {isSmallStackGame && exactChipValueUnit > 1 ? (
            <> Exact common unit is {formatChipValueCurrency(result.chipValue * exactChipValueUnit)} per {formatChipAmount(exactChipValueUnit)} chips.</>
          ) : null}
          {!isSmallStackGame && chipValueDisplayUnit !== DEFAULT_CHIP_UNIT ? (
            <> That is {formatChipValueCurrency(result.chipValue * DEFAULT_CHIP_UNIT)} per 100 chips.</>
          ) : null}
          {result.adjustmentCents !== 0 ? (
            <> Final-transfer reconciliation is {result.adjustmentCents} cents.</>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm text-muted-foreground space-y-1">
          <p>Total cash in: {formatCurrency(result.totalCashIn)}</p>
          <p>Total chips bought: {formatChipAmount(result.totalBoughtChips)}</p>
          <p>Total final chips: {formatChipAmount(result.totalFinalChips)}</p>
        </div>

        {result.warnings.length > 0 ? (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 space-y-1">
            {result.warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </div>
        ) : null}

        {result.transfers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No transfers needed. Everyone broke even.</p>
        ) : (
          <div className="space-y-2">
            {result.transfers.map((transfer, index) => (
              <div key={`${transfer.fromId}-${transfer.toId}-${index}`} className="rounded-md border p-3 text-sm">
                <span className="font-medium">{transfer.fromName}</span>
                <span className="mx-1">pays</span>
                <span className="font-medium">{transfer.toName}</span>
                <span className="mx-1">{formatCurrency(transfer.amount)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button type="button" variant="outline" onClick={onCopySummary} disabled={result.transfers.length === 0}>
            Copy summary
          </Button>
          {copyState === "done" ? <p className="text-sm text-green-600">Summary copied.</p> : null}
          {copyState === "error" ? <p className="text-sm text-destructive">Could not copy summary.</p> : null}
        </div>
      </CardContent>
    </Card>
  );
}
